import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Dialogue shadcn/ui (Radix) au système Majuscule (maquettes *Dialog.dc.html) :
 * fond `popover`, rayon lg, ombre shadow-md (réservée aux menus et dialogues),
 * 560px de large par défaut, en-tête `dialog-head` (titre 15px, description,
 * bouton fermer), corps `dialog-body` défilant, pied `dialog-foot` à filet.
 * Échap et clic sur le voile ferment ; le défilement de la page est bloqué.
 */

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

/** Voile (`scrim`) : fond assombri, sans flou. */
const overlayClass =
  'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn(overlayClass, className)} {...props} />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /** Largeur maximale en pixels (560 par défaut, 640 pour un formulaire riche). */
  width?: number;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, width = 560, style, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      style={{ width: `min(${width}px, calc(100vw - 2rem))`, ...style }}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden',
        'rounded-lg border border-border bg-popover text-popover-foreground shadow-md',
        'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        // Compense la translation de centrage pendant l'animation (tailwindcss-animate).
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'focus:outline-none',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/** Conteneur d'en-tête libre (compatibilité shadcn). Préférer `DialogHead`. */
function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div className={cn('flex flex-col gap-0.5 px-6 pt-6', className)} {...props} />;
}

interface DialogHeadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Contenu sous la description (ex. indicateur d'étapes `Stepper`). */
  extra?: React.ReactNode;
  /** Libellé du bouton de fermeture. */
  closeLabel?: string;
  /** Masque le bouton de fermeture. */
  hideClose?: boolean;
}

/** `dialog-head` : titre + description à gauche, bouton fermer (icône) à droite. */
function DialogHead({
  title,
  description,
  extra,
  closeLabel = 'Fermer',
  hideClose = false,
  className,
  ...props
}: DialogHeadProps): React.JSX.Element {
  return (
    <div
      className={cn('flex shrink-0 items-start justify-between gap-3 px-6 pt-6', className)}
      {...props}
    >
      <div className="min-w-0">
        <DialogTitle>{title}</DialogTitle>
        {description !== undefined && description !== null && description !== '' ? (
          <DialogDescription className="mt-0.5">{description}</DialogDescription>
        ) : null}
        {extra}
      </div>
      {hideClose ? null : (
        <DialogClose asChild>
          <Button variant="ghost" size="icon-sm" aria-label={closeLabel} className="-mr-1 -mt-1">
            <X aria-hidden="true" />
          </Button>
        </DialogClose>
      )}
    </div>
  );
}

/** `dialog-body` : champs espacés de 16px, défile entre l'en-tête et le pied. */
function DialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div className={cn('grid min-h-0 gap-4 overflow-y-auto px-6 py-4', className)} {...props} />
  );
}

/**
 * `dialog-foot` : actions à droite (Annuler en `outline`, validation en
 * `default` `size="lg"`) ; une action « retour » se pousse à gauche avec
 * `className="mr-auto"`.
 */
function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-3',
        className,
      )}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('m-0 text-heading', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('m-0 text-[13px] leading-[18px] text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

interface StepperProps {
  /** Étape courante, à partir de 1. */
  step: number;
  total: number;
  className?: string;
}

/** `stepper` : barres d'avancement + « Étape 2 sur 2 », sous la description d'un dialogue. */
function Stepper({ step, total, className }: StepperProps): React.JSX.Element {
  return (
    <div
      className={cn('mt-3 flex items-center gap-2 text-caption text-muted-foreground', className)}
    >
      <span className="flex gap-1" aria-hidden="true">
        {Array.from({ length: total }, (_, index) => (
          <i
            key={index}
            className={cn(
              'block h-1 w-7 rounded-full',
              index < step ? 'bg-foreground' : 'bg-muted',
            )}
          />
        ))}
      </span>
      <span>
        Étape {step} sur {total}
      </span>
    </div>
  );
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogHead,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Stepper,
  overlayClass,
};
